import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Person,
  Work,
  MoreVert,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { Doctor } from '../../types';

interface DoctorsStatsProps {
  doctors: Doctor[];
}

const DoctorsStats: React.FC<DoctorsStatsProps> = ({ doctors }) => {
  const stats = {
    total: doctors.length,
    active: doctors.filter(d => d.isActive).length,
    inactive: doctors.filter(d => !d.isActive).length,
    specializations: new Set(doctors.map(d => d.specialization)).size,
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    change: number;
    color: string;
    icon: React.ReactNode;
  }> = ({ title, value, change, color, icon }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <MoreVert />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" mb={1}>
          <Avatar sx={{ bgcolor: color, mr: 2, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          {change > 0 ? (
            <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
          ) : (
            <TrendingDown sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
          )}
          <Typography
            variant="body2"
            color={change > 0 ? 'success.main' : 'error.main'}
            sx={{ fontWeight: 500 }}
          >
            {change > 0 ? '+' : ''}{change}% last 7 days
          </Typography>
        </Box>
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.abs(change) * 10}
            sx={{
              height: 3,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Doctors"
          value={stats.total}
          change={2.6}
          color="#00A76F"
          icon={<Person />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Active Doctors"
          value={stats.active}
          change={0.2}
          color="#00A76F"
          icon={<Person />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Inactive Doctors"
          value={stats.inactive}
          change={-0.1}
          color="#FFAB00"
          icon={<Person />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Specializations"
          value={stats.specializations}
          change={1.2}
          color="#00B8D9"
          icon={<Work />}
        />
      </Grid>
    </Grid>
  );
};

export default DoctorsStats;
